import { useState, useRef } from "react"

import { toPng } from "html-to-image"
import { currentPatientFileInfoAtom } from "../Pages/Data/Visualize/DataReview/Atoms/PatientFile"
import {
	selectedLayoutAtom,
	selectedLayoutIdAtom,
} from "../Pages/Data/Visualize/DataReview/Atoms/Layout"
import { currentlyActiveVisualizationAreaAtom } from "../Pages/Data/Visualize/DataReview/Atoms/Visualizations"
import { viewScaleRegistry } from "../Pages/Data/Visualize/DataReview/Data/ViewScaleRegistry"
import { useRecoilValue } from "recoil"
import { useScreenshotProvider } from "../Providers/ScreenshotsProvider"

const OFFSCREEN_POSITION_PX = -100000

interface FontCache {
	robotoFlex: string | null
	openSans: string | null
}

const mountOffscreenClone = (
	clone: HTMLElement,
	dimensions: { width: number; height: number },
): HTMLDivElement => {
	const wrapper = document.createElement("div")
	wrapper.style.position = "fixed"
	wrapper.style.left = `${OFFSCREEN_POSITION_PX}px`
	wrapper.style.top = "0"
	wrapper.style.width = `${dimensions.width}px`
	wrapper.style.height = `${dimensions.height}px`
	wrapper.style.opacity = "0"
	wrapper.style.pointerEvents = "none"
	wrapper.appendChild(clone)
	document.body.appendChild(wrapper)
	return wrapper
}

const sanitizeLayoutName = (name: string | undefined): string =>
	name
		?.replace(/\s+/g, "-")
		?.replace(/[^a-zA-Z0-9_-]/g, "")
		?.replace(/-+/g, "-") || "unknown"

const generateScreenshotFilename = (params: {
	patientIdentifier: string
	layoutName: string
	displayStartTime: string
	displayEndTime: string
	deidentify: boolean
}): string => {
	const identifier = params.deidentify ? "DEIDENTIFIED" : params.patientIdentifier
	return `report_${identifier}_${params.displayStartTime}_${params.displayEndTime}_${params.layoutName}.png`
}

interface DisplayTimeRangeParams {
	selectedLayoutId: string | null
	activeVisualizationArea: string | null
	selectedLayout: { areas: Array<{ area: string; componentId: string }> } | null
	fileStartDate: Date | undefined
	fileEndDate: Date | undefined
}

const getDisplayTimeRange = ({
	selectedLayoutId,
	activeVisualizationArea,
	selectedLayout,
	fileStartDate,
	fileEndDate,
}: DisplayTimeRangeParams): { displayStartTime: string; displayEndTime: string } => {
	if (!selectedLayoutId || !activeVisualizationArea || !selectedLayout) {
		return { displayStartTime: "unknown", displayEndTime: "unknown" }
	}

	const activeArea = selectedLayout.areas.find(
		area => area.area === activeVisualizationArea,
	)
	if (!activeArea) {
		return { displayStartTime: "unknown", displayEndTime: "unknown" }
	}

	const layoutWindowId = {
		layoutId: selectedLayoutId,
		windowId: activeVisualizationArea,
	}
	const viewScale = viewScaleRegistry.get(layoutWindowId, activeArea.componentId, {
		fileStartDate: fileStartDate ?? new Date(0),
		fileEndDate: fileEndDate ?? new Date(0),
		viewDuration: undefined,
	})
	const [displayStart, displayEnd] = viewScale.domain()

	return { 
		displayStartTime: displayStart.toISOString(), 
		displayEndTime: displayEnd.toISOString()
	}
}

const redactSensitiveElements = (clone: HTMLElement): void => {
	const sensitiveElements = clone.querySelectorAll('[data-deidentify="true"]')
	sensitiveElements.forEach(element => {
		const valueSpan = element.querySelector("span:last-child") as HTMLElement | null
		if (valueSpan) {
			const textLength = valueSpan.textContent?.length || 0
			valueSpan.textContent = "\u00A0"
			valueSpan.style.backgroundColor = "#000"
			valueSpan.style.minWidth = `${Math.max(textLength * 8, 60)}px`
			valueSpan.style.display = "inline-block"
		}
	})
}

const createFontEmbedCSS = (robotoFlex: string, openSans: string): string => `
	@font-face {
		font-family: 'Roboto Flex';
		src: url(${robotoFlex}) format('truetype');
		font-weight: 100 1000;
		font-style: normal;
		font-display: swap;
	}
	@font-face {
		font-family: 'Open Sans';
		src: url(${openSans}) format('truetype');
		font-weight: 500;
		font-style: normal;
		font-display: swap;
	}
`

export const useScreenshots = (): {
	takingScreenshot: boolean
	takeScreenshot: (deidentify?: boolean) => Promise<void>
} => {
	const currentPatientFileInfo = useRecoilValue(currentPatientFileInfoAtom)
	const selectedLayout = useRecoilValue(selectedLayoutAtom)
	const selectedLayoutId = useRecoilValue(selectedLayoutIdAtom)
	const activeVisualizationArea = useRecoilValue(currentlyActiveVisualizationAreaAtom)
	const [isCapturing, setIsCapturing] = useState(false)
	const [isLoadingFonts, setIsLoadingFonts] = useState(false)
	const fontCacheRef = useRef<FontCache>({ robotoFlex: null, openSans: null })
	const screenshotsProvider = useScreenshotProvider()

	const loadFonts = async (): Promise<{ robotoFlex: string; openSans: string }> => {
		if (fontCacheRef.current.robotoFlex && fontCacheRef.current.openSans) {
			return {
				robotoFlex: fontCacheRef.current.robotoFlex,
				openSans: fontCacheRef.current.openSans,
			}
		}

		setIsLoadingFonts(true)
		try {
			const toBase64 = async (url: string): Promise<string> => {
			const res = await fetch(url)
			const buffer = await res.arrayBuffer()
			const bytes = new Uint8Array(buffer)
			const binary = bytes.reduce((acc, b) => acc + String.fromCharCode(b), "")
			const base64 = btoa(binary)
			return `data:font/truetype;base64,${base64}`
			}

			const [robotoFlexUrl, openSansUrl] = await Promise.all([
				import("../assets/fonts/RobotoFlex.ttf?inline"),
				import("../assets/fonts/OpenSans.ttf?inline"),
			])

			const [robotoFlex, openSans] = await Promise.all([
				toBase64(robotoFlexUrl.default),
				toBase64(openSansUrl.default),
			])

			fontCacheRef.current.robotoFlex = robotoFlex
			fontCacheRef.current.openSans = openSans

			return { robotoFlex, openSans }
		} finally {
			setIsLoadingFonts(false)
		}
	}

	const buildCaptureClone = (root: HTMLElement) => {
		const clone = root.cloneNode(true) as HTMLElement
		const originalCanvases = root.querySelectorAll("canvas")
		const clonedCanvases = clone.querySelectorAll("canvas")

		originalCanvases.forEach((canvas, index) => {
			const clonedCanvas = clonedCanvases[index]
			if (!clonedCanvas) {
				return
			}

			try {
				const dataUrl = canvas.toDataURL("image/png")
				const img = document.createElement("img")
				const rect = canvas.getBoundingClientRect()

				img.src = dataUrl
				img.width = canvas.width
				img.height = canvas.height
				img.style.cssText = canvas.style.cssText
				img.style.width = `${rect.width}px`
				img.style.height = `${rect.height}px`
				img.style.display = "block"
				img.style.objectFit = "fill"

				clonedCanvas.replaceWith(img)
			} catch (error) {
				console.error("Failed to snapshot canvas for capture", error)
			}
		})

		return clone
	}

	const takeScreenshot = async (deidentify = false) => {
		const captureRoot = document.getElementById("data-review-capture-root")

		if (!captureRoot || isCapturing || isLoadingFonts) {
			throw new Error("Unable to capture screenshot.")
		}

		setIsCapturing(true)
		let cloneWrapper: HTMLDivElement | null = null
		try {
			const { robotoFlex, openSans } = await loadFonts()

			if (document.fonts?.ready) {
				await document.fonts.load("16px 'Roboto Flex'")
				await document.fonts.load("16px 'Open Sans'")
				await document.fonts.ready
			}

			const captureClone = buildCaptureClone(captureRoot)

			if (deidentify) {
				redactSensitiveElements(captureClone)
			}

			const rect = captureRoot.getBoundingClientRect()
			cloneWrapper = mountOffscreenClone(captureClone, rect)

			await new Promise(resolve => requestAnimationFrame(resolve))

			const dataUrl = await toPng(captureClone, {
				cacheBust: true,
				pixelRatio: window.devicePixelRatio || 1,
				backgroundColor: "var(--background-color)",
				fontEmbedCSS: createFontEmbedCSS(robotoFlex, openSans),
			})

			const { displayStartTime, displayEndTime } = getDisplayTimeRange({
				selectedLayoutId,
				activeVisualizationArea,
				selectedLayout,
				fileStartDate: currentPatientFileInfo?.fileStartDate,
				fileEndDate: currentPatientFileInfo?.fileEndDate,
			})

			const now = new Date()
			const cleanLayoutName = sanitizeLayoutName(selectedLayout?.name)

			const fileName = generateScreenshotFilename({
				patientIdentifier: currentPatientFileInfo?.patientIdentifier ?? "unknown",
				layoutName: cleanLayoutName,
				displayStartTime,
				displayEndTime,
				deidentify,
			})

			const metadata = {
				fileName,
				patientIdentifier: currentPatientFileInfo?.patientIdentifier ?? "unknown_MRN",
				displayStartTime,
				displayEndTime,
				timeGenerated: now,
				displayType: cleanLayoutName,
				isDeidentified: deidentify,
				mrn: currentPatientFileInfo.patientIdentifier,
				patientIdInternal: Number(currentPatientFileInfo.patientId)
			}

			screenshotsProvider.store(dataUrl, metadata)
		} catch (error) {
			console.error("Failed to capture screenshot", error)
			throw error
		} finally {
			cloneWrapper?.parentNode?.removeChild(cloneWrapper)
			setIsCapturing(false)
		}
	}

	return { takingScreenshot: isCapturing || isLoadingFonts, takeScreenshot }
}
